import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { I } from "../../Components/CMI/icons";
import { setEmpresa, setYear, setEscenario, setMes, fetchCierres } from "../../slices/filters/reducer";

const HIDE_FILTERS_ROUTES = ["/dashboard-config", "/presupuesto", "/ivgh-config"];

const YEARS = [2024, 2025, 2026];

const ESCENARIOS = [
  { id: "proyeccion_lineal", label: "Proyección lineal" },
  { id: "presupuesto_mensual", label: "Presupuesto mensual" },
];

const MESES = [
  "Enero","Febrero","Marzo","Abril","Mayo","Junio",
  "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre",
];

const HeaderSelect = ({ ariaLabel, options, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = options.find((o) => o.id === value);

  useEffect(() => {
    if (!open) return undefined;
    const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  return (
    <div className={`hdr-select ${open ? "open" : ""}`} ref={ref} >
      <button
        type="button"
        className="hdr-select-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((o) => !o)}
      >
        <span>{current?.label ?? "—"}</span>
        <I.Chevron size={16} />
      </button>
      {open ? (
        <ul className="hdr-select-menu" role="listbox" aria-label={ariaLabel}>
          {options.map((o) => (
            <li
              key={String(o.id)}
              role="option"
              aria-selected={o.id === value}
              className={o.id === value ? "active" : ""}
              onClick={() => { onChange(o.id); setOpen(false); }}
            >
              {o.label}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

const Header = ({ onToggleSidebar }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { empresaId, year, empresasList, escenario, mes, cierres } = useSelector((s) => s.Filters);
  const CHIPS = empresasList.map(e => ({ id: e.id, label: e.label }));
  const YEAR_OPTIONS = YEARS.map(y => ({ id: y, label: String(y) }));
  const showFilters = !HIDE_FILTERS_ROUTES.some(r => location.pathname.startsWith(r));
  const isV2 = location.pathname.startsWith("/dashboard-finanzas-v2");

  useEffect(() => {
    if (isV2) dispatch(fetchCierres());
  }, [isV2, empresaId, year, dispatch]);

  const mesesCerrados = cierres.filter(c => c.estado === "cerrado").map(c => c.mes);
  const ultimoCerrado = mesesCerrados.length > 0 ? Math.max(...mesesCerrados) : null;
  const mesActual = mes || ultimoCerrado;
  const mesCerrado = mesActual != null && mesesCerrados.includes(mesActual);

  const mesLimite = ultimoCerrado != null ? Math.min(ultimoCerrado + 1, 12) : new Date().getMonth() + 1;
  const MES_OPTIONS = MESES.slice(0, mesLimite).map((label, i) => ({ id: i + 1, label }));

  return (
    <header className="hdr">
      <button className="btn btn-ghost btn-icon" onClick={onToggleSidebar} title="Plegar menú">
        <I.Bars size={18} />
      </button>
      {showFilters && (
        <>
          <div className="hdr-group">
            <span className="hdr-group-label">Empresa</span>
            <HeaderSelect
              ariaLabel="Empresa"
              options={CHIPS}
              value={empresaId}
              onChange={(id) => dispatch(setEmpresa(id))}
            />
          </div>
          <div className="hdr-group">
            <span className="hdr-group-label">Año</span>
            <HeaderSelect
              ariaLabel="Año"
              options={YEAR_OPTIONS}
              value={year}
              onChange={(y) => dispatch(setYear(y))}
            />
          </div>

          {isV2 && escenario === "presupuesto_mensual" && (
            <>
              <div className="hdr-group">
                <span className="hdr-group-label">Mes</span>
                <HeaderSelect
                  ariaLabel="Mes"
                  options={MES_OPTIONS}
                  value={mesActual}
                  onChange={(m) => dispatch(setMes(m))}
                />
              </div>
              {mesActual != null && (
                <div className="hdr-cierre-badge">
                  {mesCerrado ? (
                    <span className="badge-cerrado"><I.Check size={12} /> Mes cerrado</span>
                  ) : (
                    <span className="badge-abierto"><I.Clock size={12} /> Mes abierto</span>
                  )}
                </div>
              )}
            </>
          )}

          {isV2 && escenario === "proyeccion_lineal" && ultimoCerrado != null && (
            <div className="hdr-cierre-info">
              <I.Info size={14} />
              <span>Último mes cerrado: {MESES[ultimoCerrado - 1]} {year}</span>
            </div>
          )}
        </>
      )}
      <div className="spacer" />
      {isV2 && showFilters && (
        <div className="hdr-group">
          <span className="hdr-group-label">Escenario</span>
          <HeaderSelect
            ariaLabel="Escenario"
            options={ESCENARIOS}
            value={escenario}
            onChange={(e) => dispatch(setEscenario(e))}
          />
        </div>
      )}
    </header>
  );
};

export default Header;
